import React from "react";
import { Clock, ShieldCheck, Sparkles } from "lucide-react";
import { cn } from "@athena/ui/utils";
import { Mono } from "../primitives/typography";

export type MessageSender = "user" | "coach" | "ai" | "support" | "chat_intro" | "off_hours";

export interface ChatMessage {
    id: string;
    // "support" is a crisis-keyword auto-response (authorId: null on the backend).
    // "chat_intro" is the auto-sent first message on a new chat.
    // "off_hours" is the auto-reply sent when a user messages outside business hours.
    // "ai" is reserved for a future AI-recommendation sender kind — not sent yet.
    sender: MessageSender;
    body: string;
    time: string;
    aiLabel?: string;
}

/**
 * Whose side of the conversation the viewer is on.
 *
 * `learner` — the learner's own apps, where `sender: "user"` is the person reading.
 * `operator` — the admin inbox, where the viewer is staff and the learner is somebody else.
 *
 * It decides one thing: which side counts as "mine". Everything else follows from that.
 */
export type ChatPerspective = "learner" | "operator";

/*
 * Every bubble carries `data-sender`, the resolved role.
 *
 * So that a test can ask which role a message was drawn as without reading a class name. Which
 * side a role sits on is a design decision that changes; *which role was resolved* is the
 * behavior worth holding down — it is what broke when sender was resolved against the viewer and
 * a thread rendered as one undifferentiated voice.
 */
interface MessageBubbleProps {
    msg: ChatMessage;
    /** Defaults to `learner` — the layout every app rendered before the admin inbox needed its own. */
    perspective?: ChatPerspective;
}

/**
 * Maps a raw chat message's authorId/senderKind (as stored on the backend) to
 * the display-level MessageSender used by MessageBubble. Shared by every chat
 * UI (web ChatPage, admin InboxPage) so the mapping stays consistent.
 *
 * **This resolves the role. It does not decide the side** — `MessageBubble`'s `perspective` does
 * that, and the two apps land the same message on opposite sides on purpose.
 *
 * Hence `learnerId` rather than the viewer's id. In the learner's own apps the two are the same
 * value, which is why this was originally written as "me vs them" and went unnoticed. The admin
 * inbox broke it: a coach reads conversations they may not have written a word in, so every
 * message came back "not mine" and the thread rendered as one voice, the learner and the
 * replying coach indistinguishable. Role has to be answerable without knowing who is looking.
 */
export function resolveMessageSender(
    message: { authorId: string | null; senderKind: string },
    learnerId: string | undefined,
): MessageSender {
    switch (message.senderKind) {
        case "CRISIS_SUPPORT":
            return "support";
        case "CHAT_INTRO":
            return "chat_intro";
        case "COACH_OFF_HOURS":
            return "off_hours";
        default:
            return message.authorId === learnerId ? "user" : "coach";
    }
}

// System-message kinds each get their own icon/label/color treatment —
// mirrors the backend's ChatMessageSenderKind enum.
const SYSTEM_STYLES: Partial<
    Record<
        ChatMessage["sender"],
        {
            icon: React.ComponentType<{ className?: string }>;
            label: string;
            labelClassName: string;
            bubbleClassName: string;
            // bubbleTextColor: string;
        }
    >
> = {
    support: {
        icon: ShieldCheck,
        label: "Support message",
        labelClassName: "text-warning",
        bubbleClassName: "bg-warning/10 border-warning text-warning-foreground border",
    },
    chat_intro: {
        icon: Sparkles,
        label: "Welcome message",
        labelClassName: "text-secondary-foreground",
        bubbleClassName: "bg-primary/10 text-secondary-foreground border border-primary",
    },
    off_hours: {
        icon: Clock,
        label: "Outside business hours",
        labelClassName: "text-secondary",
        bubbleClassName: "bg-secondary/10 text-secondary-foreground border border-secondary",
    },
};

/** Coach ("Manny") avatar orb — cyan gradient with an "M" and an optional online dot. */
export function CoachOrb({ size = 32, online = false }: { size?: number; online?: boolean }) {
    return (
        <div className="relative shrink-0" style={{ width: size, height: size }}>
            <div
                className="font-display flex h-full w-full items-center justify-center rounded-full font-bold text-white"
                style={{
                    fontSize: size * 0.42,
                    background: "linear-gradient(135deg, #00C4E5 0%, #007E96 100%)",
                }}
            >
                C
            </div>
            {online ? (
                <span className="border-card absolute -right-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full border-2 bg-[#3FD07A]" />
            ) : null}
        </div>
    );
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ msg, perspective = "learner" }: MessageBubbleProps) => {
    const systemStyle = SYSTEM_STYLES[msg.sender];
    const isSystem = !!systemStyle;
    const isLearner = msg.sender === "user";

    /*
     * Your own words on the right, the other party's on the left — in both apps.
     *
     * So the same message sits on opposite sides in the learner's chat and in the admin inbox,
     * which is the point: side means "who is talking to whom", not "which role wrote this". The
     * role is still `msg.sender`, and still resolved the same way for everybody.
     *
     * Everything that is not the learner counts as the service's side and travels together — a
     * coach reply, the welcome message, a crisis auto-response. An auto-reply drawn on the
     * learner's side would read as though the learner had sent it.
     */
    const alignEnd = perspective === "learner" ? isLearner : !isLearner;

    /*
     * The tail follows the side rather than the sender, so a bubble on the right is never drawn
     * with a left-hand tail. This was two hardcoded corners that had to be kept in agreement with
     * two hardcoded alignments; deriving both from `alignEnd` is what stops them disagreeing.
     */
    const tail = alignEnd ? "rounded-br-sm" : "rounded-bl-sm";

    /*
     * Pushed with auto margins and `justify-end`, never by shrinking the wrapper.
     *
     * `max-w-[68%]` is a percentage of the bubble's containing block, so a wrapper that
     * shrink-to-fits its own content resolves that cap against a collapsed width — and a
     * two-word message comes back one word per line.
     */
    const timeRow = cn("mt-1 flex items-center gap-1.5 px-1", alignEnd && "justify-end");

    /*
     * `w-fit` so a bubble is only as wide as its text, `max-w-[68%]` so a long one still stops
     * short of the far edge. Both are needed: a block-level bubble in a stretching column fills
     * the column and the cap becomes a fixed width, which drew a two-letter "hi" as a full-width
     * bar. The percentage still measures against the full-width wrapper, which is why the wrapper
     * must not shrink to its own content.
     *
     * `wrap-anywhere` — `overflow-wrap: anywhere`, not `break-words` — because people paste
     * things with no spaces in them: a URL, a stack trace, a payload. `break-word` wraps the
     * *rendering* but leaves the element's min-content width as wide as the longest unbreakable
     * run, and `w-fit` measures that, so the bubble grew past its cap, the text spilled over the
     * rounded edge, and the whole thread scrolled sideways. `anywhere` counts the break
     * opportunities in intrinsic sizing, so the cap holds. Ordinary words still wrap on spaces —
     * a long token is the only thing it breaks mid-word.
     */
    const bubbleBox = cn("w-fit max-w-[68%] wrap-anywhere", alignEnd && "ml-auto");

    if (isSystem) {
        return (
            <div key={msg.id} className="flex flex-col">
                <div
                    className={cn(
                        "mb-1 flex items-center gap-1 px-1",
                        systemStyle.labelClassName,
                        alignEnd && "justify-end",
                    )}
                >
                    <systemStyle.icon className="h-3 w-3" />
                    <Mono className="text-[11px]">{systemStyle.label}</Mono>
                </div>

                <div
                    data-sender={msg.sender}
                    className={cn("rounded-2xl px-4 py-3", bubbleBox, systemStyle.bubbleClassName)}
                >
                    {msg.body}
                </div>
                <div className={timeRow}>
                    <Mono className="text-muted-foreground text-[11px]">{msg.time}</Mono>
                </div>
            </div>
        );
    }

    if (isLearner) {
        return (
            <div key={msg.id} className="flex flex-col">
                <div
                    data-sender={msg.sender}
                    className={cn("bg-primary text-primary-foreground rounded-2xl px-4 py-3", bubbleBox, tail)}
                >
                    {msg.body}
                </div>

                <div className={timeRow}>
                    <Mono className="text-muted-foreground text-[11px]">{msg.time}</Mono>
                </div>
            </div>
        );
    }

    return (
        <div key={msg.id}>
            <div className={cn("flex items-end gap-2", alignEnd && "justify-end")}>
                {perspective === "learner" && <CoachOrb size={28} />}
                <div
                    data-sender={msg.sender}
                    className={cn("bg-muted text-text-heading rounded-2xl border px-4 py-3", bubbleBox, tail)}
                >
                    {msg.body}
                </div>
            </div>

            <div className={timeRow}>
                <Mono className="text-muted-foreground text-[11px]">{msg.time}</Mono>
            </div>
        </div>
    );
};

export { MessageBubble };
